/**
 * PATH: packages/ui/src/kanban/components/KanbanCardInteractionControls.tsx
 * FILE_ID: UI-KANBAN-CARD-INTERACTION-001
 * PURPOSE: Compose desktop hover controls and the phone Thread trigger.
 * WHY: Phone cards need direct Thread access without hover-driven prefetch.
 * FLOW: Card/API support → Thread capability → desktop zone or phone control.
 * RELATED: KanbanCard.tsx; KanbanCardPeekHandle.tsx.
 */
import { KanbanCardActionZone } from './KanbanCardActionZone'
import { KanbanCardPeekHandle } from './KanbanCardPeekHandle'
import { KanbanCardQuickActions } from './KanbanCardQuickActions'
import { KanbanCardQuickCapture } from './KanbanCardQuickCapture'
import type { KanbanCardProps } from './KanbanCard.types'
import type { KanbanCardThreadSupport } from './kanbanCardThreadSupport'

export interface KanbanCardInteractionControlsProps
  extends Pick<
    KanbanCardProps,
    | 'api'
    | 'card'
    | 'currentUserId'
    | 'onChecklistChanged'
    | 'onContextMenu'
    | 'quickActions'
    | 'resolveMember'
  > {
  mode: 'desktop' | 'phone'
  visible: boolean
  threadSupport: KanbanCardThreadSupport
  peekChecklistReady: boolean
  onAddChecklistStep: (text: string) => Promise<void>
  /** Suspend/hide sibling hover strip while a rail picker is open. */
  onPickerOpenChange?: (open: boolean) => void
  /** Card-face prefs — hide the icon rail and/or the note + Thread row. */
  hideActionBar?: boolean
  hideCapture?: boolean
  /** Kebab follows place.more. Right-click stays on the card. */
  showMoreKebab?: boolean
}

export function KanbanCardInteractionControls({
  api,
  card,
  currentUserId,
  mode,
  onAddChecklistStep,
  onChecklistChanged,
  onContextMenu,
  peekChecklistReady,
  quickActions,
  resolveMember,
  threadSupport,
  visible,
  onPickerOpenChange,
  hideActionBar = false,
  hideCapture = false,
  showMoreKebab = true,
}: KanbanCardInteractionControlsProps) {
  const threadSlot = threadSupport.canShowThread ? (
    <KanbanCardPeekHandle
      cardId={card.id}
      cardTitle={card.title}
      api={api!}
      visible={mode === 'desktop' && visible}
      threadEnabled={threadSupport.hasAiThread}
      onAddChecklistStep={peekChecklistReady ? onAddChecklistStep : undefined}
      card={card}
      resolveMember={resolveMember}
      currentUserId={currentUserId}
    />
  ) : undefined

  if (mode === 'phone') return hideCapture ? null : threadSlot

  const quickActionsSlot =
    !hideActionBar && quickActions ? (
      <KanbanCardQuickActions
        card={card}
        {...quickActions}
        onPickerOpenChange={onPickerOpenChange}
      />
    ) : undefined
  const quickCaptureSlot =
    !hideCapture && api && quickActions ? (
      <KanbanCardQuickCapture
        card={card}
        api={api}
        visible={visible}
        currentUserId={currentUserId}
        onSetDueDate={quickActions.onSetDueDate}
        onSetActionDate={quickActions.onSetActionDate}
        onChanged={() => onChecklistChanged?.(card)}
      />
    ) : undefined
  const desktopThread = hideCapture ? undefined : threadSlot
  if (hideActionBar && hideCapture) return null

  return (
    <KanbanCardActionZone
      visible={visible}
      onMore={
        hideActionBar || !onContextMenu || !showMoreKebab
          ? undefined
          : (anchor) => onContextMenu(card, anchor)
      }
      quickActionsSlot={quickActionsSlot}
      quickCaptureSlot={quickCaptureSlot}
      threadSlot={desktopThread}
    />
  )
}
